import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useVendor } from '../contexts/VendorContext';
import LanguageSwitcher from './LanguageSwitcher';
import bookMarketLogo from '../assets/book-market-logo.png';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Icon: React.FC<{ d: string | string[] }> = ({ d }) => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    {(Array.isArray(d) ? d : [d]).map((path, i) => (
      <path key={i} strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
    ))}
  </svg>
);

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { isAdmin, isPendingVendor, pendingOrders } = useVendor();

  const navigation = isPendingVendor
    ? [
        { name: t('nav.profile'), href: '/profile', icon: <Icon d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /> },
      ]
    : [
        { name: t('nav.dashboard'), href: '/dashboard', icon: <Icon d="M4 5a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z" /> },
        { name: t('nav.scanner'), href: '/scanner', icon: <Icon d={["M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z", "M15 13a3 3 0 11-6 0 3 3 0 016 0z"]} /> },
        { name: t('nav.inventory'), href: '/inventory', icon: <Icon d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /> },
        { name: t('nav.orders'), href: '/orders', badge: pendingOrders, icon: <Icon d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /> },
        { name: t('nav.profile'), href: '/profile', icon: <Icon d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /> },
        ...(isAdmin
          ? [{ name: t('nav.admin'), href: '/admin', icon: <Icon d="M12 11c1.657 0 3-1.343 3-3S13.657 5 12 5 9 6.343 9 8s1.343 3 3 3zm0 2c-3.866 0-7 1.79-7 4v2h14v-2c0-2.21-3.134-4-7-4z" /> }]
          : []),
      ];

  const isActive = (path: string) => location.pathname === path || (path !== '/admin' && location.pathname.startsWith(path + '/')) || (path === '/admin' && location.pathname.startsWith('/admin'));

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-30 lg:hidden transition-opacity" onClick={onClose} />
      )}

      <div
        className={`fixed left-0 z-40 lg:z-20 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } top-0 bottom-0 lg:top-16`}
      >
        <div className="flex flex-col h-full overflow-y-auto">
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
            <Link to="/" onClick={onClose} className="flex items-center">
              <img src={bookMarketLogo} alt="BookMarketLk" className="h-9 w-auto" />
            </Link>
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-md text-gray-600 active:text-gray-900 active:bg-gray-100 min-w-[44px] min-h-[44px] touch-manipulation"
              aria-label="Close sidebar"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <nav className="flex-1 px-3 sm:px-4 py-4 space-y-1 sm:space-y-2">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={onClose}
                className={`flex items-center px-3 sm:px-4 py-3 text-sm sm:text-base font-medium rounded-lg transition-colors min-h-[44px] touch-manipulation ${
                  isActive(item.href) ? 'bg-blue-50 text-blue-700' : 'text-gray-700 active:bg-gray-50'
                }`}
              >
                <span className={`mr-3 ${isActive(item.href) ? 'text-blue-600' : 'text-gray-500'}`}>{item.icon}</span>
                <span className="flex-1">{item.name}</span>
                {'badge' in item && item.badge ? (
                  <span className="ml-2 min-w-[22px] h-[22px] px-1.5 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center">
                    {item.badge}
                  </span>
                ) : null}
              </Link>
            ))}
          </nav>

          <div className="px-3 sm:px-4 py-3 sm:py-4 border-t border-gray-200 space-y-3">
            <div className="sm:hidden">
              <LanguageSwitcher className="w-full" />
            </div>
            <p className="text-xs text-gray-500">{t('nav.version')}</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
